import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "@formspree/react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";

const BUSINESS_TYPES = [
  "Café / Food / Hospitality",
  "Trades / Services",
  "Retail / Shop",
  "Health / Wellness / Beauty",
  "Professional Services",
  "Something else",
];

const FOUND_VIA = [
  "Facebook",
  "Instagram",
  "Google",
  "Word of mouth",
  "A directory or local listing",
  "Nowhere really",
];

const PROBLEMS = [
  "I spend way too many hours doing everything myself",
  "I know nothing about tech and don't have time to learn",
  "I know I need it but don't know what to do or where to start",
  "I've always thought it would cost a fortune",
  "I want more time off, more time with family",
  "I work too many hours and want to work smarter not harder",
  "I want to make more money but don't know where to start online",
  "I'm losing customers to competitors with better websites",
  "My current website is embarrassing or outdated",
  "I'm starting fresh and want to do it right",
  "Something else",
];

const WINS = [
  "Not working so many hours",
  "Making more money",
  "More time off",
  "More time with family",
  "More enquiries coming in while I sleep",
  "Look professional when people Google me",
  "Stop relying on Facebook as my only presence",
  "Free up my time with better systems online",
  "Get found by people in my area",
  "Something else",
];

const TIME_PREFS = [
  "Morning before 10am",
  "Mid morning 10am–12pm",
  "Arvo 12pm–5pm",
  "Evening after 5pm",
  "Anytime",
];

const REFERRAL_OPTIONS = [
  "You drove through my town",
  "Google search",
  "Facebook or Instagram",
  "Someone referred me",
  "Other",
];

function SectionHeading({ number, title, subtitle }: { number: string; title: string; subtitle?: string }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-1">
        <span className="inline-flex items-center justify-center rounded-full text-xs font-bold"
          style={{ width: 28, height: 28, background: "#6FAFB5", color: "#fff", flexShrink: 0 }}>
          {number}
        </span>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.25rem", fontWeight: 600, color: "#1F2E3B", margin: 0 }}>
          {title}
        </h2>
      </div>
      {subtitle && <p className="text-sm mt-1 ml-10" style={{ color: "#536270" }}>{subtitle}</p>}
    </div>
  );
}

function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="block text-sm font-medium mb-1.5" style={{ color: "#1F2E3B" }}>
      {children}
      {required && <span style={{ color: "#6FAFB5", marginLeft: 2 }}>*</span>}
    </label>
  );
}

const inputClass = "w-full rounded-xl px-4 py-3 text-sm border outline-none transition-all focus:ring-2";
const inputStyle = { borderColor: "#d6cfc5", background: "#fff", color: "#1F2E3B" };
const inputFocusClass = "focus:border-teal-400";

function TextInput({ name, placeholder, required, value, onChange, type = "text" }: {
  name: string; placeholder?: string; required?: boolean; value: string; onChange: (v: string) => void; type?: string;
}) {
  return (
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      required={required}
      value={value}
      onChange={e => onChange(e.target.value)}
      data-testid={`input-${name}`}
      className={`${inputClass} ${inputFocusClass}`}
      style={{ ...inputStyle, borderWidth: 1 }}
    />
  );
}

function RadioGroup({ name, options, value, onChange }: {
  name: string; options: string[]; value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      {options.map(opt => (
        <label key={opt} className="flex items-center gap-3 cursor-pointer group" data-testid={`radio-${name}-${opt.slice(0, 12).replace(/\s/g, "-")}`}>
          <span className="flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all"
            style={{ width: 20, height: 20, borderColor: value === opt ? "#6FAFB5" : "#c8c0b5", background: value === opt ? "#6FAFB5" : "#fff" }}>
            {value === opt && <span style={{ width: 8, height: 8, background: "#fff", borderRadius: "50%", display: "block" }} />}
          </span>
          <input type="radio" name={name} value={opt} checked={value === opt} onChange={() => onChange(opt)} className="sr-only" />
          <span className="text-sm" style={{ color: "#1F2E3B" }}>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function CheckboxGroup({ name, options, values, onChange }: {
  name: string; options: string[]; values: string[]; onChange: (v: string[]) => void;
}) {
  function toggle(opt: string) {
    onChange(values.includes(opt) ? values.filter(v => v !== opt) : [...values, opt]);
  }
  return (
    <div className="flex flex-col gap-2.5">
      {options.map(opt => {
        const checked = values.includes(opt);
        return (
          <label key={opt} className="flex items-center gap-3 cursor-pointer" data-testid={`check-${name}-${opt.slice(0, 12).replace(/\s/g, "-")}`}>
            <span className="flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all"
              style={{ width: 20, height: 20, borderColor: checked ? "#6FAFB5" : "#c8c0b5", background: checked ? "#6FAFB5" : "#fff", flexShrink: 0 }}>
              {checked && (
                <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                  <path d="M1 4l3 3 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <input type="checkbox" name={name} value={opt} checked={checked} onChange={() => toggle(opt)} className="sr-only" />
            <span className="text-sm leading-snug" style={{ color: "#1F2E3B" }}>{opt}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function Check() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);

  // Section 1
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [businessTypeOther, setBusinessTypeOther] = useState("");
  const [townState, setTownState] = useState("");

  // Section 2
  const [hasWebsite, setHasWebsite] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [foundVia, setFoundVia] = useState<string[]>([]);
  const [directoryName, setDirectoryName] = useState("");

  // Section 3
  const [problems, setProblems] = useState<string[]>([]);
  const [problemOther, setProblemOther] = useState("");

  // Section 4
  const [wins, setWins] = useState<string[]>([]);
  const [winOther, setWinOther] = useState("");

  // Section 5
  const [contactPref, setContactPref] = useState("");
  const [phone, setPhone] = useState("");
  const [timePref, setTimePref] = useState("");
  const [visitAddress, setVisitAddress] = useState("");

  // Referral
  const [referral, setReferral] = useState("");

  // @formspree/react handles the Formspree POST
  const [formspreeState, formspreeSubmit] = useForm("xykbbnyq");

  // Backend mutation for confirmation emails
  const mutation = useMutation({
    mutationFn: (data: Record<string, any>) => apiRequest("POST", "/api/check", data),
    onError: () => toast({ title: "Something went wrong", description: "Please try again or email us at hello@bizdr.app", variant: "destructive" }),
  });

  // When Formspree confirms receipt → show success page
  useEffect(() => {
    if (formspreeState.succeeded) setSubmitted(true);
  }, [formspreeState.succeeded]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const resolvedBusinessType = businessType === "Something else" ? `Something else: ${businessTypeOther}` : businessType;
    const resolvedFoundVia     = foundVia.map(f => f === "A directory or local listing" && directoryName ? `A directory or local listing (${directoryName})` : f);
    const resolvedProblems     = problems.map(p => p === "Something else" ? `Something else: ${problemOther}` : p);
    const resolvedWins         = wins.map(w => w === "Something else" ? `Something else: ${winOther}` : w);
    const resolvedPhone        = contactPref === "Call me" ? phone : "";
    const resolvedVisitAddress = contactPref === "Come find me when you're nearby" ? visitAddress : "";

    const extraParts = [
      email && `Email: ${email}`,
      resolvedPhone && `Phone: ${resolvedPhone}`,
      timePref && `Best time: ${timePref}`,
      resolvedVisitAddress && `Visit address: ${resolvedVisitAddress}`,
      referral && `How they heard: ${referral}`,
      hasWebsite === "Yes" && websiteUrl && `Website: ${websiteUrl}`,
    ].filter(Boolean).join(" | ");

    // 1 — Open Claude immediately with all data as query params
    const params = new URLSearchParams({
      bizdr:         "1",
      businessName,
      ownerName:     name,
      businessType:  resolvedBusinessType,
      location:      townState,
      currentOnline: resolvedFoundVia.join(", "),
      mainServices:  resolvedBusinessType,
      painPoints:    resolvedProblems.join(", "),
      goals:         resolvedWins.join(", "),
      extras:        extraParts,
    });
    window.open(`https://claude.ai/new?${params.toString()}`, "_blank");

    // 2 — Submit to Formspree via @formspree/react (reads hidden inputs below)
    formspreeSubmit(e as any);

    // 3 — Send via backend for confirmation + admin notification emails
    mutation.mutate({
      name,
      businessName,
      email,
      businessType:  resolvedBusinessType,
      townState,
      hasWebsite,
      websiteUrl:    hasWebsite === "Yes" ? websiteUrl : "",
      foundVia:      resolvedFoundVia,
      problems:      resolvedProblems,
      wins:          resolvedWins,
      contactPref,
      phone:         resolvedPhone,
      timePref:      contactPref === "Call me" ? timePref : "",
      visitAddress:  resolvedVisitAddress,
      referral,
    });
  }

  const cardStyle = { background: "#fff", border: "1px solid #e4ddd2", borderRadius: 20 };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "#F7F4EE", fontFamily: "'DM Sans', sans-serif", color: "#1F2E3B" }}>
        <Nav />
        <main className="flex-1 flex items-center justify-center px-6 py-20">
          <div className="max-w-md w-full text-center">
            <div className="text-5xl mb-6">🐶</div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "2rem", fontWeight: 600, color: "#1F2E3B", marginBottom: 16 }}>
              Got it. We're on it.
            </h1>
            <p style={{ color: "#536270", lineHeight: 1.7, marginBottom: 24 }}>
              I'll have a proper look at your business online and come back to you within 24 hours with something worth looking at. Check your inbox — there should be a confirmation from us now.
            </p>
            <Link href="/" style={{ display: "inline-flex", borderRadius: 999, padding: "12px 28px", background: "#6FAFB5", color: "#fff", textDecoration: "none", fontSize: "0.9rem", fontWeight: 500 }}>
              Back to BizDr
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#F7F4EE", fontFamily: "'DM Sans', sans-serif", color: "#1F2E3B" }}>
      <Nav />

      <main className="mx-auto max-w-2xl px-6 py-12 pb-20">
        {/* Page header */}
        <div className="mb-10">
          <div className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: "#6FAFB5", letterSpacing: "0.12em" }}>Free · No obligation</div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem,4vw,2.8rem)", fontWeight: 600, lineHeight: 1.2, color: "#1F2E3B", marginBottom: 12 }}>
            Your BizDr Check
          </h1>
          <p style={{ color: "#536270", lineHeight: 1.7, fontSize: "1rem", maxWidth: 520 }}>
            Fill this in and I'll take a proper look at your business online — then come back to you within 24 hours with something worth looking at. No pitch, no pressure.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-6">

            {/* ── Section 1 ── */}
            <div style={cardStyle} className="p-7">
              <SectionHeading number="1" title="About You" />
              <div className="flex flex-col gap-5">
                <div>
                  <Label required>Your name</Label>
                  <TextInput name="name" placeholder="First and last name" required value={name} onChange={setName} />
                </div>
                <div>
                  <Label required>Business name</Label>
                  <TextInput name="businessName" placeholder="What do people know you as?" required value={businessName} onChange={setBusinessName} />
                </div>
                <div>
                  <Label required>Your email</Label>
                  <TextInput name="email" type="email" placeholder="Where should I send your check?" required value={email} onChange={setEmail} />
                </div>
                <div>
                  <Label>What kind of business?</Label>
                  <RadioGroup name="businessType" options={BUSINESS_TYPES} value={businessType} onChange={setBusinessType} />
                  {businessType === "Something else" && (
                    <div className="mt-3 ml-7">
                      <TextInput name="businessTypeOther" placeholder="Tell me a bit about it" value={businessTypeOther} onChange={setBusinessTypeOther} />
                    </div>
                  )}
                </div>
                <div>
                  <Label required>Town and state you're based in</Label>
                  <TextInput name="townState" placeholder="e.g. Broken Hill, NSW" required value={townState} onChange={setTownState} />
                </div>
              </div>
            </div>

            {/* ── Section 2 ── */}
            <div style={cardStyle} className="p-7">
              <SectionHeading number="2" title="Your Online Presence" />
              <div className="flex flex-col gap-5">
                <div>
                  <Label>Do you have a website?</Label>
                  <RadioGroup name="hasWebsite" options={["Yes", "Sort of — it's not great", "No — nothing yet"]} value={hasWebsite} onChange={setHasWebsite} />
                  {hasWebsite === "Yes" && (
                    <div className="mt-3 ml-7">
                      <TextInput name="websiteUrl" placeholder="What's the URL?" value={websiteUrl} onChange={setWebsiteUrl} />
                    </div>
                  )}
                </div>
                <div>
                  <Label>Where do people find you now?</Label>
                  <CheckboxGroup name="foundVia" options={FOUND_VIA} values={foundVia} onChange={setFoundVia} />
                  {foundVia.includes("A directory or local listing") && (
                    <div className="mt-3 ml-7">
                      <TextInput name="directoryName" placeholder="Which one?" value={directoryName} onChange={setDirectoryName} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Section 3 ── */}
            <div style={cardStyle} className="p-7">
              <SectionHeading number="3" title="What's the Problem?" subtitle="Tick everything that feels true" />
              <CheckboxGroup name="problems" options={PROBLEMS} values={problems} onChange={setProblems} />
              {problems.includes("Something else") && (
                <div className="mt-3 ml-7">
                  <TextInput name="problemOther" placeholder="Tell me more" value={problemOther} onChange={setProblemOther} />
                </div>
              )}
            </div>

            {/* ── Section 4 ── */}
            <div style={cardStyle} className="p-7">
              <SectionHeading number="4" title="What Would a Win Look Like?" subtitle="Tick all that apply" />
              <CheckboxGroup name="wins" options={WINS} values={wins} onChange={setWins} />
              {wins.includes("Something else") && (
                <div className="mt-3 ml-7">
                  <TextInput name="winOther" placeholder="Tell me more" value={winOther} onChange={setWinOther} />
                </div>
              )}
            </div>

            {/* ── Section 5 ── */}
            <div style={cardStyle} className="p-7">
              <SectionHeading number="5" title="How Do You Want to Connect?" />
              <RadioGroup
                name="contactPref"
                options={["Email me when you have something to show me", "Call me", "Come find me when you're nearby"]}
                value={contactPref}
                onChange={setContactPref}
              />
              {contactPref === "Call me" && (
                <div className="mt-4 ml-7 flex flex-col gap-3">
                  <div>
                    <Label>Phone number</Label>
                    <TextInput name="phone" placeholder="Best number to reach you" value={phone} onChange={setPhone} />
                  </div>
                  <div>
                    <Label>Best time to call</Label>
                    <select
                      value={timePref}
                      onChange={e => setTimePref(e.target.value)}
                      data-testid="select-timePref"
                      className={`${inputClass}`}
                      style={{ ...inputStyle, borderWidth: 1 }}>
                      <option value="">Select a time…</option>
                      {TIME_PREFS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              )}
              {contactPref === "Come find me when you're nearby" && (
                <div className="mt-4 ml-7">
                  <Label>Your address or area</Label>
                  <TextInput name="visitAddress" placeholder="Where should we head?" value={visitAddress} onChange={setVisitAddress} />
                </div>
              )}
            </div>

            {/* ── Referral ── */}
            <div style={cardStyle} className="p-7">
              <Label>How did you hear about BizDr?</Label>
              <select
                value={referral}
                onChange={e => setReferral(e.target.value)}
                data-testid="select-referral"
                className={`${inputClass} mt-1`}
                style={{ ...inputStyle, borderWidth: 1 }}>
                <option value="">Select one…</option>
                {REFERRAL_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            {/* Hidden inputs — read by @formspree/react on submit */}
            <input type="hidden" name="businessName"  value={businessName} />
            <input type="hidden" name="ownerName"     value={name} />
            <input type="hidden" name="businessType"  value={businessType === "Something else" ? `Something else: ${businessTypeOther}` : businessType} />
            <input type="hidden" name="location"      value={townState} />
            <input type="hidden" name="currentOnline" value={foundVia.map(f => f === "A directory or local listing" && directoryName ? `A directory or local listing (${directoryName})` : f).join(", ")} />
            <input type="hidden" name="mainServices"  value={businessType === "Something else" ? `Something else: ${businessTypeOther}` : businessType} />
            <input type="hidden" name="painPoints"    value={problems.map(p => p === "Something else" ? `Something else: ${problemOther}` : p).join(", ")} />
            <input type="hidden" name="goals"         value={wins.map(w => w === "Something else" ? `Something else: ${winOther}` : w).join(", ")} />
            <input type="hidden" name="extras" value={[email && `Email: ${email}`, contactPref === "Call me" && phone && `Phone: ${phone}`, timePref && `Best time: ${timePref}`, contactPref === "Come find me when you're nearby" && visitAddress && `Visit address: ${visitAddress}`, referral && `How they heard: ${referral}`, hasWebsite === "Yes" && websiteUrl && `Website: ${websiteUrl}`].filter(Boolean).join(" | ")} />

            {/* ── Submit ── */}
            <div className="flex flex-col items-start gap-4">
              <button
                type="submit"
                disabled={mutation.isPending || !name || !businessName || !email || !townState}
                data-testid="button-submit-check"
                className="rounded-full px-8 py-4 text-sm font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "#1F2E3B", color: "#fff", fontSize: "1rem" }}>
                {mutation.isPending ? "Sending…" : "Send my BizDr Check"}
              </button>
              <p style={{ fontStyle: "italic", fontSize: "0.8rem", color: "#8a9098", lineHeight: 1.6, maxWidth: 480 }}>
                I'll come back to you within 24 hours with something worth looking at. No pitch, no pressure. Just honest advice about what would actually help your business.
              </p>
            </div>

          </div>
        </form>
      </main>

      {/* Simple footer */}
      <footer style={{ borderTop: "1px solid #e4ddd2", background: "#F7F4EE", padding: "24px 24px" }}>
        <div className="mx-auto max-w-2xl flex items-center justify-between text-xs" style={{ color: "#8a9098" }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1rem", color: "#1F2E3B" }}>
            Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
          </span>
          <span>© {new Date().getFullYear()} BizDr · <a href="/privacy" style={{ color: "#6FAFB5", textDecoration: "none" }}>Privacy</a></span>
        </div>
      </footer>
    </div>
  );
}

function Nav() {
  return (
    <nav style={{ background: "rgba(247,244,238,.95)", backdropFilter: "blur(14px)", borderBottom: "1px solid #e4ddd2", position: "sticky", top: 0, zIndex: 50 }}>
      <div className="mx-auto max-w-2xl px-6 py-3 flex items-center justify-between">
        <Link href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }} data-testid="link-check-logo">
          <svg width="28" height="16" viewBox="0 0 28 16" fill="none" aria-hidden="true">
            <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8" stroke="#6FAFB5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.4rem", letterSpacing: "-0.01em", color: "#1F2E3B", lineHeight: 1 }}>
            Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
          </span>
        </Link>
        <Link href="/" style={{ fontSize: "0.85rem", color: "#536270", textDecoration: "none" }} data-testid="link-back-home">
          ← Back to BizDr
        </Link>
      </div>
    </nav>
  );
}
